enum RelatedObject {
    DOCUMENT = 'document',
    CONTACT = 'contact',
    CLIENT = 'client',
    PROSPECT = 'prospect',
    SUPPLIER = 'supplier',
    PRODUCT = 'product',
    SERVICE = 'service',
    TICKET = 'ticket',
    TASK = 'task',
    PURCHASE = 'purchase',
    OPPORTUNITY = 'opportunity',
    PROJECT = 'project',
    STAFF = 'staff',
    SITE = 'site',
}

export enum CustomFieldType {
    SIMPLE_TEXT = 'simple-text',
    RICH_TEXT = 'rich-text',
    EMAIL = 'email',
    URL = 'url',
    NUMERIC = 'numeric',
    DATE = 'date',
    TIME = 'time',
    AMOUNT = 'amount',
    NUMERIC_WITH_UNIT = 'numeric-with-unit',
    RADIO = 'radio',
    SELECT = 'select',
    CHECKBOX = 'checkbox',
    BOOLEAN = 'boolean',
    COMPANY_INDIVIDUAL = 'company-individual',
    CONTACT = 'contact',
    STAFF = 'staff',
    ITEM = 'item',
}

enum UnitType {
    UNIT = 'unit',
    PACKAGE = 'package',
    M2 = 'm2',
    M3 = 'm3',
    TON = 'ton',
    KG = 'kg',
    G = 'g',
    METRES = 'metres',
    DAYS = 'days',
    HOUR = 'hour',
    MINUTES = 'minutes',
}

interface BaseCustomField {
    id: number;
    name: string;
    code: string;
    description: string;
    mandatory: boolean;
    rank: number;
    related_object: RelatedObject[];
    customfield_group: {
        id: number;
        name: string;
        code: string;
        open_by_default: boolean;
    };
    show_on_pdf: boolean;
    type: CustomFieldType;
    parameters: Record<string, unknown>;
    value: unknown;
    error?: string | null;
}

export interface RelationValueItem {
    id: number;
    label: string;
}

export interface SimpleTextCustomField extends BaseCustomField {
    type: CustomFieldType.SIMPLE_TEXT;
    parameters: {
        default_value: string | null;
        char_min: number | null;
        char_max: number | null;
    };
    value: string | null;
}

export interface RichTextCustomField extends BaseCustomField {
    type: CustomFieldType.RICH_TEXT;
    parameters: {
        default_value: string | null;
    };
    value: string | null;
}

export interface EmailCustomField extends BaseCustomField {
    type: CustomFieldType.EMAIL;
    parameters: {
        default_value: string | null;
    };
    value: string | null;
}

export interface UrlCustomField extends BaseCustomField {
    type: CustomFieldType.URL;
    parameters: {
        default_value: string | null;
    };
    value: string | null;
}

export interface NumericCustomField extends BaseCustomField {
    type: CustomFieldType.NUMERIC;
    parameters: {
        default_value: string | null;
        min_value: string | null;
        max_value: string | null;
    };
    value: string | null;
}

export interface DateCustomField extends BaseCustomField {
    type: CustomFieldType.DATE;
    parameters: {
        min_date: string | null;
        max_date: string | null;
    };
    value: string | null;
}

export interface TimeCustomField extends BaseCustomField {
    type: CustomFieldType.TIME;
    parameters: {
        min_hour: string | null;
        max_hour: string | null;
    };
    value: string | null;
}

export interface AmountCustomField extends BaseCustomField {
    type: CustomFieldType.AMOUNT;
    parameters: {
        default_value: string | null;
        min_value: string | null;
        max_value: string | null;
        currency: string | null;
    };
    value: {
        amount: string | null;
        currency: string | null;
    };
}

export interface NumericWithUnitCustomField extends BaseCustomField {
    type: CustomFieldType.NUMERIC_WITH_UNIT;
    parameters: {
        default_value: string | null;
        min_value: string | null;
        max_value: string | null;
        unit: UnitType | null;
    };
    value: {
        number: string | null;
        unit_id: number | null;
    };
}

interface ChoiceType {
    id: number;
    label: string;
    checked: boolean;
    rank: number;
}

export interface RadioCustomField extends BaseCustomField {
    type: CustomFieldType.RADIO;
    parameters: {
        items: ChoiceType[];
    };
    value: number | null;
}

export interface SelectCustomField extends BaseCustomField {
    type: CustomFieldType.SELECT;
    parameters: {
        items: ChoiceType[];
    };
    value: number | null;
}

export interface CheckboxCustomField extends BaseCustomField {
    type: CustomFieldType.CHECKBOX;
    parameters: {
        items: ChoiceType[];
        max: number | null;
        min: number | null;
    };
    value: number[];
}

export interface BooleanCustomField extends BaseCustomField {
    type: CustomFieldType.BOOLEAN;
    parameters: {
        default_value: boolean | null;
    };
    value: boolean | null;
}

interface ThirdType {
    id: number;
    type: string;
}

interface AdditionalCompanyData {
    id: number;
    name: string;
    type: string;
}

interface AdditionalIndividualData {
    id: number;
    first_name: string;
    last_name: string;
    type: string;
}

export interface CompanyIndividualCustomField extends BaseCustomField {
    type: CustomFieldType.COMPANY_INDIVIDUAL;
    parameters: {
        number_of_values: number;
        client: boolean;
        prospect: boolean;
        supplier: boolean;
        address_on_pdf: boolean;
        email_on_pdf: boolean;
        phone_on_pdf: boolean;
        mobile_on_pdf: boolean;
    };
    value: ThirdType[] | null;
    _embed: {
        value_details: AdditionalCompanyData[] | AdditionalIndividualData[];
    };
}

interface AdditionalContactData {
    id: number;
    first_name: string;
    last_name: string;
}

export interface ContactCustomField extends BaseCustomField {
    type: CustomFieldType.CONTACT;
    parameters: {
        number_of_values: number;
        address_on_pdf: boolean;
        email_on_pdf: boolean;
        phone_on_pdf: boolean;
        mobile_on_pdf: boolean;
    };
    value: number[] | null;
    _embed: {
        value_details: AdditionalContactData[];
    };
}

interface AdditionalStaffData {
    id: number;
    first_name: string;
    last_name: string;
}

export interface StaffCustomField extends BaseCustomField {
    type: CustomFieldType.STAFF;
    parameters: {
        number_of_values: number;
        email_on_pdf: boolean;
        phone_on_pdf: boolean;
        mobile_on_pdf: boolean;
    };
    value: number[] | null;
    _embed: {
        value_details: AdditionalStaffData[];
    };
}

interface AdditionalItemData {
    id: number;
    number: string;
    name: string | null;
}

export interface ItemCustomField extends BaseCustomField {
    type: CustomFieldType.ITEM;
    parameters: {
        number_of_values: number;
        product: boolean;
        service: boolean;
    };
    value: number[] | null;
    _embed: {
        value_details: AdditionalItemData[];
    };
}

export type CustomField =
    | SimpleTextCustomField
    | RichTextCustomField
    | EmailCustomField
    | UrlCustomField
    | NumericCustomField
    | DateCustomField
    | TimeCustomField
    | AmountCustomField
    | NumericWithUnitCustomField
    | RadioCustomField
    | SelectCustomField
    | CheckboxCustomField
    | BooleanCustomField
    | CompanyIndividualCustomField
    | ContactCustomField
    | StaffCustomField
    | ItemCustomField;

export interface CustomFieldGroup {
    id: number;
    name: string;
    openByDefault: boolean;
    customFields: CustomField[];
}
